import React from 'react';
import Markdown, { Components, defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';

/**
 * Quote description (Markdown) for the customer dialog and the admin preview.
 * Raw HTML is never rendered; links only http(s) / mailto.
 */

const ALLOWED = ['p', 'strong', 'em', 'del', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'blockquote', 'hr', 'a', 'br'];

const urlTransform = (url: string): string =>
    /^(https?:|mailto:)/i.test(url.trim()) ? defaultUrlTransform(url) : '';

const heading = (variant: 'subtitle1' | 'subtitle2'): Components['h2'] =>
    ({ children }) => <Typography variant={variant} component="div" sx={{ fontWeight: 'bold', mt: 1.5, mb: 0.5, color: 'inherit' }}>{children}</Typography>;

const components: Components = {
    p: ({ children }) => <Typography variant="body2" sx={{ mb: 1, color: 'inherit' }}>{children}</Typography>,
    h1: heading('subtitle1'),
    h2: heading('subtitle1'),
    h3: heading('subtitle2'),
    h4: heading('subtitle2'),
    a: ({ href, children }) => href
        ? <Link href={href} target="_blank" rel="noopener noreferrer nofollow">{children}</Link>
        : <>{children}</>,
    ul: ({ children }) => <Box component="ul" sx={{ pl: 3, my: 1, typography: 'body2', color: 'inherit' }}>{children}</Box>,
    ol: ({ children }) => <Box component="ol" sx={{ pl: 3, my: 1, typography: 'body2', color: 'inherit' }}>{children}</Box>,
    li: ({ children }) => <Box component="li" sx={{ mb: 0.5, '& p': { mb: 0 } }}>{children}</Box>,
    blockquote: ({ children }) => (
        <Box component="blockquote" sx={{ m: 0, my: 1, pl: 1.5, borderLeft: 3, borderColor: 'grey.400', color: '#555' }}>{children}</Box>
    ),
    hr: () => <Divider sx={{ my: 1.5 }} />,
};

const QuoteMarkdown: React.FC<{ markdown: string }> = ({ markdown }) => (
    // Theme body1/body2 are light grey (dark surfaces)
    <Box sx={{ color: '#1a1a1a', wordBreak: 'break-word' }}>
        <Markdown
            remarkPlugins={[remarkGfm]}
            skipHtml
            allowedElements={ALLOWED}
            unwrapDisallowed
            urlTransform={urlTransform}
            components={components}
        >
            {markdown}
        </Markdown>
    </Box>
);

export default QuoteMarkdown;
